import type React from "react"

import { AlertTriangle, ArrowDown, ArrowUp, Target } from "lucide-react"

interface ForecastData {
  target: number
  forecast?: number
  closedWon: number
  committed: number
  bestCase: number
  worstCase?: number
  pipeline: number
  attainment: number
  previousPeriodChange: number
}

interface ForecastSummaryProps {
  forecastData: ForecastData
}

// Custom Card Components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white text-black rounded-lg border border-gray-200 shadow-sm ${className}`}>{children}</div>
)

const CardHeader = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
)

const CardContent = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`px-6 pb-4 ${className}`}>{children}</div>
)

const CardTitle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <h3 className={`text-lg font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
)

const CardDescription = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <p className={`text-sm text-gray-600 ${className}`}>{children}</p>
)

// Custom Progress Component
const Progress = ({ value, className = "" }: { value: number; className?: string }) => (
  <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${className}`}>
    <div
      className="bg-blue-600 h-full transition-all duration-300 ease-in-out"
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
)

export function ForecastSummary({ forecastData }: ForecastSummaryProps) {
  const { target, committed, bestCase, pipeline, closedWon, attainment, previousPeriodChange } = forecastData
  const worstCase = forecastData.worstCase || Math.round(committed * 0.8)
  const forecast = forecastData.forecast || Math.round(committed * 1.1)

  const forecastProgress = Math.min(100, Math.round((forecast / target) * 100))
  const closedProgress = Math.min(100, Math.round((closedWon / target) * 100))
  const commitProgress = Math.min(100, Math.round((committed / target) * 100))
  const bestCaseProgress = Math.min(100, Math.round((bestCase / target) * 100))
  const worstCaseProgress = Math.min(100, Math.round((worstCase / target) * 100))

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Forecast Summary</CardTitle>
            <CardDescription>Current period forecast and pipeline health</CardDescription>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="font-medium">vs Previous Period:</span>
            <span
              className={
                previousPeriodChange >= 0 ? "text-green-600 flex items-center" : "text-red-600 flex items-center"
              }
            >
              {previousPeriodChange >= 0 ? (
                <ArrowUp className="h-4 w-4 mr-1" />
              ) : (
                <ArrowDown className="h-4 w-4 mr-1" />
              )}
              {Math.abs(previousPeriodChange)}%
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium text-gray-600">Quota</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex items-center">
                <Target className="h-5 w-5 mr-2 text-blue-600" />
                <span className="text-2xl font-bold">${(target / 1000).toFixed(0)}k</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium text-gray-600">Forecast</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex items-center">
                <span className="text-lg mr-2">🎱</span>
                <span className="text-2xl font-bold">${(forecast / 1000).toFixed(0)}k</span>
              </div>
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>of Quota</span>
                  <span className="font-medium">{forecastProgress}%</span>
                </div>
                <Progress value={forecastProgress} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium text-gray-600">Closed</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex items-center">
                <span className="text-lg mr-2">🏆</span>
                <span className="text-2xl font-bold">${(closedWon / 1000).toFixed(0)}k</span>
              </div>
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>of Quota</span>
                  <span className="font-medium">{closedProgress}%</span>
                </div>
                <Progress value={closedProgress} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium text-gray-600">Committed</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex items-center">
                <span className="text-lg mr-2">👍</span>
                <span className="text-2xl font-bold">${(committed / 1000).toFixed(0)}k</span>
              </div>
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>of Quota</span>
                  <span className="font-medium">{commitProgress}%</span>
                </div>
                <Progress value={commitProgress} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium text-gray-600">Best Case</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex items-center">
                <span className="text-lg mr-2">🤞</span>
                <span className="text-2xl font-bold">${(bestCase / 1000).toFixed(0)}k</span>
              </div>
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>of Quota</span>
                  <span className="font-medium">{bestCaseProgress}%</span>
                </div>
                <Progress value={bestCaseProgress} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-sm font-medium text-gray-600">Worst Case</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2 text-amber-600" />
                <span className="text-2xl font-bold">${(worstCase / 1000).toFixed(0)}k</span>
              </div>
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>of Quota</span>
                  <span className="font-medium">{worstCaseProgress}%</span>
                </div>
                <Progress value={worstCaseProgress} className="h-2" />
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  )
}
